import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import AddressBookService from '../services/AddressBookService';
import AddressDisplay, { AddressDisplayLong, notifyAddressBookUpdated } from './AddressDisplay';
import { walletManager, WALLET_TYPES, getAccounts } from './EthersSetup';
import WalletSelection from './basics/WalletSelection';
import './GameMaker.css'; // For collapsible-box styles

const PersonalSettings = () => {
  const [addressEntries, setAddressEntries] = useState([]);
  const [newAddress, setNewAddress] = useState('');
  const [newNickname, setNewNickname] = useState('');
  const [editingAddress, setEditingAddress] = useState(null);
  const [editingNickname, setEditingNickname] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [isAddressBookCollapsed, setIsAddressBookCollapsed] = useState(false);
  const [isImportExportCollapsed, setIsImportExportCollapsed] = useState(true);
  const [isWalletManagementCollapsed, setIsWalletManagementCollapsed] = useState(false);
  const [isPrivateKeyManagementCollapsed, setIsPrivateKeyManagementCollapsed] = useState(true);
  const [showWalletSelection, setShowWalletSelection] = useState(false);
  const [currentWalletInfo, setCurrentWalletInfo] = useState(null);
  const [walletAccounts, setWalletAccounts] = useState([]);
  const [persistenceInfo, setPersistenceInfo] = useState(null);
  const fileInputRef = useRef(null);

  useEffect(() => {
    loadAddressEntries();
    loadWalletInfo();
  }, []);

  const loadWalletInfo = async () => {
    try {
      const activeProvider = walletManager.getActiveProvider();
      if (activeProvider) {
        const accounts = await getAccounts();
        const walletType = activeProvider.getType();
        
        setWalletAccounts(accounts);
        setCurrentWalletInfo({
          type: walletType,
          address: accounts[0] || null,
          isConnected: !!accounts[0]
        });

        // Load persistence info for private key wallets
        if (walletType === WALLET_TYPES.CUSTOM_RPC && activeProvider.getPersistenceInfo) {
          const persistenceData = activeProvider.getPersistenceInfo();
          setPersistenceInfo(persistenceData);
        } else {
          setPersistenceInfo(null);
        }
      } else {
        setCurrentWalletInfo(null);
        setWalletAccounts([]);
        setPersistenceInfo(null);
      }
    } catch (error) {
      console.error('Error loading wallet info:', error);
      setCurrentWalletInfo(null);
      setWalletAccounts([]);
      setPersistenceInfo(null);
    }
  };

  const loadAddressEntries = () => {
    const entries = AddressBookService.getAllEntries();
    setAddressEntries(entries);
  };

  const clearMessages = () => {
    setError('');
    setSuccess('');
  };

  const showError = (message) => {
    setError(message);
    setSuccess('');
    setTimeout(clearMessages, 5000);
  };

  const showSuccess = (message) => {
    setSuccess(message);
    setError('');
    setTimeout(clearMessages, 3000);
  };

  const handleAddAddress = () => {
    clearMessages();
    
    if (!newAddress.trim()) {
      showError('Please enter an address');
      return;
    }
    
    if (!newNickname.trim()) {
      showError('Please enter a nickname');
      return;
    }

    if (!AddressBookService.isValidAddress(newAddress)) {
      showError('Please enter a valid wallet address (0x followed by 40 hex characters)');
      return;
    }

    const success = AddressBookService.setNickname(newAddress, newNickname);
    if (success) {
      setNewAddress('');
      setNewNickname('');
      loadAddressEntries();
      notifyAddressBookUpdated();
      showSuccess(`Added nickname "${newNickname}" for address`);
    } else {
      showError('Failed to add address nickname');
    }
  };

  const handleEditStart = (address, nickname) => {
    setEditingAddress(address);
    setEditingNickname(nickname);
    clearMessages();
  };

  const handleEditSave = () => {
    clearMessages();
    
    if (!editingNickname.trim()) {
      showError('Nickname cannot be empty');
      return;
    }

    const success = AddressBookService.setNickname(editingAddress, editingNickname);
    if (success) {
      setEditingAddress(null);
      setEditingNickname('');
      loadAddressEntries();
      notifyAddressBookUpdated();
      showSuccess('Nickname updated successfully');
    } else {
      showError('Failed to update nickname');
    }
  };

  const handleEditCancel = () => {
    setEditingAddress(null);
    setEditingNickname('');
    clearMessages();
  };

  const handleDelete = (address) => {
    if (window.confirm('Are you sure you want to remove this address nickname?')) {
      const success = AddressBookService.removeNickname(address);
      if (success) {
        loadAddressEntries();
        notifyAddressBookUpdated();
        showSuccess('Address nickname removed');
      } else {
        showError('Failed to remove address nickname');
      }
    }
  };

  const handleClearAll = () => {
    if (window.confirm('Are you sure you want to remove ALL address nicknames? This cannot be undone.')) {
      AddressBookService.clearAll();
      loadAddressEntries();
      notifyAddressBookUpdated();
      showSuccess('All address nicknames cleared');
    }
  };

  const handleExport = () => {
    try {
      const jsonData = AddressBookService.exportAddressBook();
      const blob = new Blob([jsonData], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      
      const a = document.createElement('a');
      a.href = url;
      a.download = `gamemaker-address-book-${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      
      showSuccess('Address book exported successfully');
    } catch (error) {
      showError('Failed to export address book');
      console.error('Export error:', error);
    }
  };

  const handleImportFile = (event) => {
    const file = event.target.files[0];
    if (!file) return;
    
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const result = AddressBookService.importAddressBook(e.target.result, true);
        if (result.success) {
          loadAddressEntries();
          notifyAddressBookUpdated();
          showSuccess(result.message);
        } else {
          showError(result.message);
        }
      } catch (error) {
        showError('Failed to read import file');
      }
      
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    };
    
    reader.readAsText(file);
  };

  const handleDisconnectWallet = async () => {
    const walletType = currentWalletInfo?.type;
    const isPrivateKey = walletType === WALLET_TYPES.CUSTOM_RPC;
    
    let confirmMessage = 'Are you sure you want to disconnect your wallet?';
    
    if (isPrivateKey) {
      const activeProvider = walletManager.getActiveProvider();
      const isPersistent = activeProvider?.isPersistenceEnabled?.() || false;
      
      if (isPersistent) {
        confirmMessage = 'Are you sure you want to disconnect and delete your saved private key? This will remove the key from browser storage permanently and you will need to re-enter it to reconnect.';
      } else {
        confirmMessage = 'Are you sure you want to disconnect? Your private key will be removed from memory and you will need to re-enter it to reconnect.';
      }
    }
    
    if (window.confirm(confirmMessage)) {
      try {
        await walletManager.disconnect();
        await loadWalletInfo();
        
        if (isPrivateKey) {
          const activeProvider = walletManager.getActiveProvider();
          const wasPersistent = activeProvider?.isPersistenceEnabled?.() || false;
          showSuccess(wasPersistent ? 'Saved private key deleted and wallet disconnected' : 'Private key removed from memory and wallet disconnected');
        } else {
          showSuccess('Wallet disconnected successfully');
        }
      } catch (error) {
        showError('Failed to disconnect wallet: ' + error.message);
      }
    }
  };

  const handleSwitchWallet = () => {
    setShowWalletSelection(true);
  };

  const handleWalletConnected = async () => {
    setShowWalletSelection(false);
    await loadWalletInfo();
    showSuccess('Wallet connected successfully');
  };

  const handleWalletError = (error) => {
    showError('Wallet connection failed: ' + error);
    setShowWalletSelection(false);
  };

  const getWalletTypeDisplay = (type) => {
    switch (type) {
      case WALLET_TYPES.METAMASK:
        return 'MetaMask';
      case WALLET_TYPES.CUSTOM_RPC:
        return 'Custom RPC Wallet';
      default:
        return 'Unknown';
    }
  };

  // Custom RPC Wallet Management Functions
  const handleClearSavedKey = async () => {
    const confirmMessage = 'Are you sure you want to delete your saved private key?\n\nThis will permanently remove it from browser storage. You will need to re-enter your private key to reconnect.';
    
    if (window.confirm(confirmMessage)) {
      try {
        const activeProvider = walletManager.getActiveProvider();
        if (activeProvider && activeProvider.clearSavedKey) {
          const success = await activeProvider.clearSavedKey();
          if (success) {
            await loadWalletInfo(); // Refresh persistence info
            showSuccess('Saved private key deleted successfully');
          } else {
            showError('Failed to delete saved private key');
          }
        } else {
          showError('Unable to delete saved key - provider not available');
        }
      } catch (error) {
        console.error('Error clearing saved key:', error);
        showError('Failed to delete saved private key: ' + error.message);
      }
    }
  };

  const handleDisconnectKeepKey = async () => {
    const confirmMessage = 'Disconnect wallet but keep the saved private key?\n\nYour key will remain saved for quick reconnection next time.';
    
    if (window.confirm(confirmMessage)) {
      try {
        const activeProvider = walletManager.getActiveProvider();
        if (activeProvider && activeProvider.disconnect) {
          await activeProvider.disconnect(false); // forceClear = false
          await loadWalletInfo();
          showSuccess('Wallet disconnected. Your private key remains saved.');
        } else {
          await walletManager.disconnect();
          await loadWalletInfo();
          showSuccess('Wallet disconnected');
        }
      } catch (error) {
        console.error('Error disconnecting:', error);
        showError('Failed to disconnect: ' + error.message);
      }
    }
  };

  const handleDisconnectDeleteKey = async () => {
    const confirmMessage = 'Disconnect wallet and permanently delete saved private key?\n\nYou will need to re-enter your private key to reconnect.';
    
    if (window.confirm(confirmMessage)) {
      try {
        const activeProvider = walletManager.getActiveProvider();
        if (activeProvider && activeProvider.disconnect) {
          await activeProvider.disconnect(true); // forceClear = true
          await loadWalletInfo();
          showSuccess('Wallet disconnected and private key deleted');
        } else {
          await walletManager.disconnect();
          await loadWalletInfo();
          showSuccess('Wallet disconnected');
        }
      } catch (error) {
        console.error('Error disconnecting and deleting key:', error);
        showError('Failed to disconnect and delete key: ' + error.message);
      }
    }
  };

  const hasSavedPrivateKey = () => {
    const activeProvider = walletManager.getActiveProvider();
    return activeProvider && activeProvider.hasSavedKey && activeProvider.hasSavedKey();
  };

  return (
    <div className="container max-w-4xl mx-auto p-6">
      <div className="flex justify-between items-center mb-8">
        <h1 className="text-4xl font-bold text-gray-800">Personal Settings</h1>
        <Link 
          to="/" 
          className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded-md transition"
        >
          ← Back to Home
        </Link>
      </div>

      {/* Messages */}
      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-6">
          {error}
        </div>
      )}
      
      {success && (
        <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded mb-6">
          {success}
        </div>
      )}

      {/* Show wallet selection overlay */}
      {showWalletSelection && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl p-6 max-w-md w-full mx-4">
            <WalletSelection
              onWalletConnected={handleWalletConnected}
              onError={handleWalletError}
            />
            <div className="mt-4 text-center">
              <button
                onClick={() => setShowWalletSelection(false)}
                className="text-gray-500 hover:text-gray-700 text-sm underline"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Wallet Management Section */}
      <div className={`collapsible-box ${isWalletManagementCollapsed ? 'collapsed' : 'expanded'} mb-6`}>
        <div 
          className="collapsible-header cursor-pointer"
          onClick={() => setIsWalletManagementCollapsed(!isWalletManagementCollapsed)}
        >
          <div className="flex justify-between items-center">
            <div className="text-lg font-bold">
              Wallet Management {currentWalletInfo ? `(${getWalletTypeDisplay(currentWalletInfo.type)})` : '(Not Connected)'}
            </div>
            <div className="text-sm text-gray-500">
              {isWalletManagementCollapsed ? '+' : '-'}
            </div>
          </div>
        </div>
        
        {!isWalletManagementCollapsed && (
          <div className="collapsible-content mt-4">
            <p className="text-gray-600 mb-4">
              Manage your wallet connection and view current wallet information.
            </p>
            
            {currentWalletInfo ? (
              <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-4">
                <div className="flex items-center mb-3">
                  <div className="w-3 h-3 bg-green-500 rounded-full mr-2"></div>
                  <h3 className="text-lg font-semibold text-green-800">
                    Connected via {getWalletTypeDisplay(currentWalletInfo.type)}
                  </h3>
                </div>
                
                <div className="space-y-2">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Current Address:</label>
                    <div className="font-mono text-sm bg-gray-100 p-2 rounded break-all">
                      <AddressDisplay address={currentWalletInfo.address} copyable={true} />
                    </div>
                  </div>
                  
                  {currentWalletInfo.type === WALLET_TYPES.CUSTOM_RPC && (
                    <div className="bg-yellow-50 border border-yellow-200 rounded p-3 mt-3">
                      <div className="flex items-start">
                        <svg className="h-5 w-5 text-yellow-400 mt-0.5 mr-2 flex-shrink-0" viewBox="0 0 20 20" fill="currentColor">
                          <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                        </svg>
                        <div>
                          <p className="text-sm font-medium text-yellow-800">Wallet Security</p>
                          <div className="text-xs text-yellow-700 mt-1 space-y-1">
                            {(() => {
                              const activeProvider = walletManager.getActiveProvider();
                              const isPersistent = activeProvider?.isPersistenceEnabled?.() || false;
                              return (
                                <>
                                  <p>• Storage: {isPersistent ? 'Browser localStorage (obfuscated)' : 'Memory only (current session)'}</p>
                                  <p>• Session timeout: {isPersistent ? '8 hours' : '30 minutes'}</p>
                                  <p>• Disconnecting will {isPersistent ? 'delete saved key and' : ''} remove from {isPersistent ? 'storage and ' : ''}memory</p>
                                </>
                              );
                            })()}
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
                
                <div className="flex space-x-3 mt-4">
                  <button
                    onClick={handleSwitchWallet}
                    className="px-4 py-2 bg-blue-500 text-white text-sm rounded hover:bg-blue-600 transition"
                  >
                    Switch Wallet
                  </button>
                  <button
                    onClick={handleDisconnectWallet}
                    className="px-4 py-2 bg-red-500 text-white text-sm rounded hover:bg-red-600 transition"
                  >
                    {(() => {
                      if (currentWalletInfo.type === WALLET_TYPES.CUSTOM_RPC) {
                        const activeProvider = walletManager.getActiveProvider();
                        const isPersistent = activeProvider?.isPersistenceEnabled?.() || false;
                        return isPersistent ? 'Delete Saved Key & Disconnect' : 'Delete Key & Disconnect';
                      }
                      return 'Disconnect';
                    })()}
                  </button>
                </div>
              </div>
            ) : (
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 mb-4">
                <div className="flex items-center mb-3">
                  <div className="w-3 h-3 bg-gray-400 rounded-full mr-2"></div>
                  <h3 className="text-lg font-semibold text-gray-600">No Wallet Connected</h3>
                </div>
                <p className="text-sm text-gray-600 mb-3">
                  Connect a wallet to interact with the GameMaker platform.
                </p>
                <button
                  onClick={handleSwitchWallet}
                  className="px-4 py-2 bg-blue-500 text-white text-sm rounded hover:bg-blue-600 transition"
                >
                  Connect Wallet
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Custom RPC Wallet Management Section */}
      {currentWalletInfo && currentWalletInfo.type === WALLET_TYPES.CUSTOM_RPC && (
        <div className={`collapsible-box ${isPrivateKeyManagementCollapsed ? 'collapsed' : 'expanded'} mb-6`}>
          <div 
            className="collapsible-header cursor-pointer"
            onClick={() => setIsPrivateKeyManagementCollapsed(!isPrivateKeyManagementCollapsed)}
          >
            <div className="flex justify-between items-center">
              <div className="text-lg font-bold">
                Custom RPC Wallet Management
              </div>
              <div className="text-sm text-gray-500">
                {isPrivateKeyManagementCollapsed ? '+' : '-'}
              </div>
            </div>
          </div>
          
          {!isPrivateKeyManagementCollapsed && (
            <div className="collapsible-content mt-4">
              <p className="text-gray-600 mb-4">
                Manage your saved private key and storage preferences.
              </p>
              
              {persistenceInfo && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                  <h3 className="text-lg font-semibold text-blue-800 mb-3">Current Storage Status</h3>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                    <div>
                      <label className="block font-medium text-blue-700">Storage Mode:</label>
                      <span className={`font-semibold ${
                        persistenceInfo.mode === 'indefinite' ? 'text-green-600' :
                        persistenceInfo.mode === 'timed' ? 'text-orange-600' : 'text-gray-600'
                      }`}>
                        {persistenceInfo.mode === 'indefinite' ? 'Indefinite (Until manually deleted)' :
                         persistenceInfo.mode === 'timed' ? 'Timed (8 hours)' : 'Session Only (30 minutes)'}
                      </span>
                    </div>
                    
                    <div>
                      <label className="block font-medium text-blue-700">Connected Since:</label>
                      <span className="text-blue-600">
                        {persistenceInfo.connectionTime ? 
                          new Date(persistenceInfo.connectionTime).toLocaleString() : 'Unknown'}
                      </span>
                    </div>
                    
                    {persistenceInfo.sessionTimeoutMinutes && (
                      <div>
                        <label className="block font-medium text-blue-700">Session Timeout:</label>
                        <span className="text-blue-600">
                          {persistenceInfo.sessionTimeoutMinutes === null ? 'Never' : 
                           `${persistenceInfo.sessionTimeoutMinutes} minutes`}
                        </span>
                      </div>
                    )}
                    
                    <div>
                      <label className="block font-medium text-blue-700">Key Saved in Browser:</label>
                      <span className={`font-semibold ${hasSavedPrivateKey() ? 'text-green-600' : 'text-gray-600'}`}>
                        {hasSavedPrivateKey() ? 'Yes' : 'No'}
                      </span>
                    </div>
                  </div>
                </div>
              )}

              {/* Management Actions */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Disconnect Options */}
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h4 className="font-semibold text-gray-800 mb-2">Disconnect Options</h4>
                  
                  {persistenceInfo?.isIndefinite ? (
                    <div className="space-y-2">
                      <button
                        onClick={handleDisconnectKeepKey}
                        className="w-full px-4 py-2 bg-gray-500 text-white text-sm rounded hover:bg-gray-600 transition"
                      >
                        Disconnect (Keep Key Saved)
                      </button>
                      <button
                        onClick={handleDisconnectDeleteKey}
                        className="w-full px-4 py-2 bg-red-500 text-white text-sm rounded hover:bg-red-600 transition"
                      >
                        Disconnect & Delete Key
                      </button>
                      <p className="text-xs text-gray-600 mt-2">
                        With indefinite storage, you can choose to keep your key saved for next time.
                      </p>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      <button
                        onClick={handleDisconnectWallet}
                        className="w-full px-4 py-2 bg-red-500 text-white text-sm rounded hover:bg-red-600 transition"
                      >
                        Disconnect & Clear Key
                      </button>
                      <p className="text-xs text-gray-600">
                        {persistenceInfo?.mode === 'timed' ? 
                          'Timed keys are cleared when disconnecting.' : 
                          'Session-only keys are cleared when disconnecting.'}
                      </p>
                    </div>
                  )}
                </div>

                {/* Key Management */}
                {hasSavedPrivateKey() && (
                  <div className="bg-red-50 border border-red-200 p-4 rounded-lg">
                    <h4 className="font-semibold text-red-800 mb-2">Saved Key Management</h4>
                    <button
                      onClick={handleClearSavedKey}
                      className="w-full px-4 py-2 bg-red-500 text-white text-sm rounded hover:bg-red-600 transition mb-2"
                    >
                      Delete Saved Wallet Data
                    </button>
                    <p className="text-xs text-red-700">
                      This will permanently remove your private key from browser storage.
                      You can continue using your current session.
                    </p>
                  </div>
                )}
              </div>

              {/* Security Information */}
              <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                <div className="flex items-start">
                  <svg className="h-5 w-5 text-yellow-400 mt-0.5 mr-2 flex-shrink-0" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                  </svg>
                  <div>
                    <h4 className="text-sm font-medium text-yellow-800 mb-1">Security Reminder</h4>
                    <ul className="text-xs text-yellow-700 space-y-1">
                      <li>• Private keys are stored with basic obfuscation, not encryption</li>
                      <li>• Only save keys on devices you fully control</li>
                      <li>• Clearing browser data will delete all saved keys</li>
                      <li>• Consider using session-only mode on shared computers</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Address Book Section */}
      <div className={`collapsible-box ${isAddressBookCollapsed ? 'collapsed' : 'expanded'} mb-6`}>
        <div 
          className="collapsible-header cursor-pointer"
          onClick={() => setIsAddressBookCollapsed(!isAddressBookCollapsed)}
        >
          <div className="flex justify-between items-center">
            <div className="text-lg font-bold">Address Book ({addressEntries.length} entries)</div>
            <div className="text-sm text-gray-500">
              {isAddressBookCollapsed ? '+' : '-'}
            </div>
          </div>
        </div>
        
        {!isAddressBookCollapsed && (
          <div className="collapsible-content mt-4">
            <p className="text-gray-600 mb-4">
              Manage human-readable nicknames for blockchain addresses. These will be displayed throughout the application instead of long addresses.
            </p>
            
            {/* Add New Address */}
            <div className="bg-gray-50 p-4 rounded-lg mb-6">
              <h3 className="text-lg font-semibold mb-3">Add New Address</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Blockchain Address
                  </label>
                  <input
                    type="text"
                    value={newAddress}
                    onChange={(e) => setNewAddress(e.target.value)}
                    placeholder="0x1234567890123456789012345678901234567890"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Nickname
                  </label>
                  <input
                    type="text"
                    value={newNickname}
                    onChange={(e) => setNewNickname(e.target.value)}
                    placeholder="Alice, Bob, My Wallet, etc."
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>
              <button
                onClick={handleAddAddress}
                className="bg-blue-500 text-white px-4 py-2 rounded-md hover:bg-blue-600 transition"
              >
                Add Address
              </button>
            </div>

            {/* Address List */}
            {addressEntries.length === 0 ? (
              <div className="text-center text-gray-500 py-8">
                No addresses saved yet. Add your first address nickname above!
              </div>
            ) : (
              <div className="space-y-3">
                <div className="flex justify-between items-center">
                  <h3 className="text-lg font-semibold">Saved Addresses</h3>
                  <button
                    onClick={handleClearAll}
                    className="bg-red-500 text-white px-3 py-1 text-sm rounded-md hover:bg-red-600 transition"
                  >
                    Clear All
                  </button>
                </div>
                
                {addressEntries.map((entry) => (
                  <div key={entry.address} className="border rounded-lg p-3 bg-white">
                    {editingAddress === entry.address ? (
                      <div className="flex items-center space-x-3">
                        <input
                          type="text"
                          value={editingNickname}
                          onChange={(e) => setEditingNickname(e.target.value)}
                          className="flex-1 px-2 py-1 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                        <button
                          onClick={handleEditSave}
                          className="bg-green-500 text-white px-3 py-1 text-sm rounded hover:bg-green-600"
                        >
                          Save
                        </button>
                        <button
                          onClick={handleEditCancel}
                          className="bg-gray-500 text-white px-3 py-1 text-sm rounded hover:bg-gray-600"
                        >
                          Cancel
                        </button>
                      </div>
                    ) : (
                      <div className="flex justify-between items-center">
                        <div className="flex-1">
                          <div className="font-medium text-gray-800">{entry.nickname}</div>
                          <div className="text-sm font-mono text-gray-600 break-all">
                            <AddressDisplay address={entry.address} copyable={true} />
                          </div>
                        </div>
                        <div className="flex space-x-2">
                          <button
                            onClick={() => handleEditStart(entry.address, entry.nickname)}
                            className="bg-blue-500 text-white px-3 py-1 text-sm rounded hover:bg-blue-600"
                          >
                            Edit
                          </button>
                          <button
                            onClick={() => handleDelete(entry.address)}
                            className="bg-red-500 text-white px-3 py-1 text-sm rounded hover:bg-red-600"
                          >
                            Delete
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Import/Export Section */}
      <div className={`collapsible-box ${isImportExportCollapsed ? 'collapsed' : 'expanded'}`}>
        <div 
          className="collapsible-header cursor-pointer"
          onClick={() => setIsImportExportCollapsed(!isImportExportCollapsed)}
        >
          <div className="flex justify-between items-center">
            <div className="text-lg font-bold">Import/Export Address Book</div>
            <div className="text-sm text-gray-500">
              {isImportExportCollapsed ? '+' : '-'}
            </div>
          </div>
        </div>
        
        {!isImportExportCollapsed && (
          <div className="collapsible-content mt-4">
            <p className="text-gray-600 mb-4">
              Backup your address book or share it between devices.
            </p>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Export */}
              <div className="bg-gray-50 p-4 rounded-lg">
                <h3 className="text-lg font-semibold mb-2">Export</h3>
                <p className="text-sm text-gray-600 mb-3">
                  Download your address book as a JSON file.
                </p>
                <button
                  onClick={handleExport}
                  disabled={addressEntries.length === 0}
                  className={`w-full py-2 px-4 rounded-md transition ${
                    addressEntries.length === 0
                      ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                      : 'bg-green-500 text-white hover:bg-green-600'
                  }`}
                >
                  Export Address Book
                </button>
              </div>

              {/* Import */}
              <div className="bg-gray-50 p-4 rounded-lg">
                <h3 className="text-lg font-semibold mb-2">Import</h3>
                <p className="text-sm text-gray-600 mb-3">
                  Import addresses from a previously exported JSON file. This will merge with existing entries.
                </p>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".json"
                  onChange={handleImportFile}
                  className="w-full py-2 px-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PersonalSettings;